import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ApplyTemplateResult,
  LinkPreview,
  SyncPreview,
  SyncRequest,
  SyncResult,
  WorkClassificationTemplate,
  WorkClassificationTemplateDetail,
  WorkClassificationTemplateInput,
  WorkClassificationTemplateNode,
  WorkClassificationTemplateNodeInput,
} from './work-classification-template.models';

@Injectable({ providedIn: 'root' })
export class WorkClassificationTemplateService {
  private http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiRoot}WorkClassificationTemplates`;

  list(includeInactive = false): Observable<WorkClassificationTemplate[]> {
    const params = new HttpParams().set('includeInactive', includeInactive);
    return this.http.get<WorkClassificationTemplate[]>(this.apiUrl, { params });
  }

  get(id: number): Observable<WorkClassificationTemplateDetail> {
    return this.http.get<WorkClassificationTemplateDetail>(`${this.apiUrl}/${id}`);
  }

  create(input: WorkClassificationTemplateInput): Observable<WorkClassificationTemplateDetail> {
    return this.http.post<WorkClassificationTemplateDetail>(this.apiUrl, input);
  }

  createFromProject(projectId: number, input: WorkClassificationTemplateInput): Observable<WorkClassificationTemplateDetail> {
    return this.http.post<WorkClassificationTemplateDetail>(`${this.apiUrl}/fromProject/${projectId}`, input);
  }

  update(id: number, input: WorkClassificationTemplateInput): Observable<WorkClassificationTemplateDetail> {
    return this.http.put<WorkClassificationTemplateDetail>(`${this.apiUrl}/${id}`, input);
  }

  setDefault(id: number): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/default`, null);
  }

  setActive(id: number, isActive: boolean): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/active`, { isActive });
  }

  /** Soft delete (deactivate). */
  deactivate(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  addNode(id: number, input: WorkClassificationTemplateNodeInput): Observable<WorkClassificationTemplateNode> {
    return this.http.post<WorkClassificationTemplateNode>(`${this.apiUrl}/${id}/nodes`, input);
  }

  updateNode(id: number, nodeId: number, input: WorkClassificationTemplateNodeInput): Observable<WorkClassificationTemplateNode> {
    return this.http.put<WorkClassificationTemplateNode>(`${this.apiUrl}/${id}/nodes/${nodeId}`, input);
  }

  deleteNode(id: number, nodeId: number): Observable<{ removed: number }> {
    return this.http.delete<{ removed: number }>(`${this.apiUrl}/${id}/nodes/${nodeId}`);
  }

  /** The client only ever applies onlyIfEmpty; replaceAll is deliberately not exposed. */
  apply(templateId: number, projectId: number): Observable<ApplyTemplateResult> {
    const params = new HttpParams().set('mode', 'onlyIfEmpty');
    return this.http.post<ApplyTemplateResult>(`${this.apiUrl}/${templateId}/apply/${projectId}`, null, { params });
  }

  linkPreview(templateId: number, projectId: number): Observable<LinkPreview> {
    return this.http.get<LinkPreview>(`${this.apiUrl}/${templateId}/link-preview/${projectId}`);
  }

  /** Omit projectNodeIds to link every match. */
  link(templateId: number, projectId: number, projectNodeIds?: number[]): Observable<{ linked: number }> {
    const body = projectNodeIds ? { projectNodeIds } : null;
    return this.http.post<{ linked: number }>(`${this.apiUrl}/${templateId}/link/${projectId}`, body);
  }

  syncPreview(templateId: number, projectId: number): Observable<SyncPreview> {
    return this.http.get<SyncPreview>(`${this.apiUrl}/${templateId}/sync-preview/${projectId}`);
  }

  /** The backend requires an explicit selection body; there is no "apply all". */
  sync(templateId: number, projectId: number, request: SyncRequest): Observable<SyncResult> {
    return this.http.post<SyncResult>(`${this.apiUrl}/${templateId}/sync/${projectId}`, request);
  }
}
